import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ApiClient } from '@fixterconnect/core';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { formatPhoneNumber } from '../utils/phoneFormat';

const Signup: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userType, setUserType] = useState<'client' | 'contractor'>('client');
  const [yearsExperience, setYearsExperience] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mode toggle state for signup/login switching
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showLoginLink, setShowLoginLink] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const { login } = useAuth();
  const apiClient = new ApiClient(API_BASE_URL);

  const switchToLogin = () => {
    setMode('login');
    setEmailError(null);
    setShowLoginLink(false);
    setError('');
    // Focus password after mode switch
    setTimeout(() => passwordInputRef.current?.focus(), 0);
  };

  const switchToSignup = () => {
    setMode('signup');
    setEmailError(null);
    setShowLoginLink(false);
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      if (user) {
        navigate((user as any).accountType === 'contractor' ? '/contractor-dashboard' : '/client-dashboard');
      } else {
        setError('Invalid email or password');
        setIsLoading(false);
      }
    } catch (error) {
      setError('An error occurred during login. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    if (userType === 'contractor' && yearsExperience && Number(yearsExperience) < 0) {
      setError('Years of experience cannot be negative');
      setIsLoading(false);
      return;
    }

    try {
      const userData = {
        firstName,
        lastName,
        email,
        phone,
        username: email, // Use email as username
        password,
        accountType: userType,
        ...(userType === 'contractor' && {
          ...(yearsExperience && { yearsExperience: Number(yearsExperience) }),
          ...(businessName && { businessName })
        })
      };

      const response = await apiClient.createAccount(userData);

      if (response.success) {
        setSuccess(true);
        // Auto-login the user after successful signup
        const loggedInUser = await login(email, password);
        if (loggedInUser) {
          // Redirect to appropriate dashboard based on user type
          navigate(userType === 'contractor' ? '/contractor-dashboard' : '/client-dashboard');
        } else {
          // Fallback to login page if auto-login fails
          navigate('/login', {
            state: { message: 'Account created successfully! Please log in.' }
          });
        }
      } else {
        // Check for duplicate email error
        if (response.error === 'An account with this email already exists') {
          setEmailError('An account with this email already exists.');
          setShowLoginLink(true);
        } else {
          setError(response.error || 'Failed to create account');
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError('An error occurred during signup. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '32px', textAlign: 'center' }}>
          {mode === 'signup' ? 'Create Account' : 'Log In'}
        </h1>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            Account created successfully! Signing you in...
          </div>
        )}

        <form onSubmit={mode === 'signup' ? handleSubmit : handleLogin}>
          {mode === 'signup' && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '16px', color: '#64748b' }}>
              Account Type
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setUserType('client')}
                style={{
                  flex: 1,
                  padding: '16px',
                  backgroundColor: userType === 'client' ? '#3b82f6' : '#f8fafc',
                  color: userType === 'client' ? 'white' : '#64748b',
                  border: userType === 'client' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Client
              </button>
              <button
                type="button"
                onClick={() => setUserType('contractor')}
                style={{
                  flex: 1,
                  padding: '16px',
                  backgroundColor: userType === 'contractor' ? '#3b82f6' : '#f8fafc',
                  color: userType === 'contractor' ? 'white' : '#64748b',
                  border: userType === 'contractor' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Contractor
              </button>
            </div>
          </div>
          )}

          {mode === 'signup' && (
          <>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              First Name *
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Last Name *
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
          </div>
          </>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(null);
                setShowLoginLink(false);
              }}
              required
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '12px',
                border: emailError ? '1px solid #dc2626' : '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
            {emailError && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginTop: '6px'
              }}>
                {emailError}
                {showLoginLink && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={switchToLogin}
                      style={{
                        color: '#2563eb',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: 'inherit'
                      }}
                    >
                      Log in instead
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {mode === 'signup' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Phone *
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              required
              placeholder="(555) 123-4567"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
          </div>
          )}

          {mode === 'signup' && userType === 'contractor' && (
            <>
              <div style={{
                borderTop: '1px solid #e2e8f0',
                marginTop: '32px',
                marginBottom: '24px',
                paddingTop: '24px'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '24px'
                }}>
                  Contractor Information
                </h2>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#64748b' }}>
                    Business Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Your business name"
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#1e293b'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#64748b' }}>
                    Years of Experience
                  </label>
                  <input
                    type="number"
                    value={yearsExperience}
                    onChange={(e) => setYearsExperience(e.target.value)}
                    min="0"
                    placeholder="Years in business"
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#1e293b'
                    }}
                  />
                </div>

              </div>
            </>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                ref={passwordInputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'signup' ? 6 : undefined}
                placeholder={mode === 'signup' ? 'At least 6 characters' : 'Enter your password'}
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '44px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '44px',
                  minWidth: '44px'
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {mode === 'signup' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Confirm Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Re-enter your password"
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '44px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '44px',
                  minWidth: '44px'
                }}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          )}

          {mode === 'signup' && (
          <div style={{ marginBottom: '24px', marginTop: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              padding: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '2px solid #e2e8f0'
            }}>
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                required
                style={{
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  accentColor: '#3b82f6'
                }}
              />
              <span style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5' }}>
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: '500' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: '500' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
          )}

          <button
            type="submit"
            disabled={mode === 'signup' ? (isLoading || success || !agreeToTerms) : isLoading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: success ? '#10b981' : '#ff9900',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: (mode === 'signup' ? (isLoading || success || !agreeToTerms) : isLoading) ? 'not-allowed' : 'pointer',
              opacity: (mode === 'signup' ? (isLoading || success || !agreeToTerms) : isLoading) ? 0.6 : 1
            }}
          >
            {mode === 'signup'
              ? (isLoading ? 'Creating Account...' : success ? 'Account Created!' : 'Sign Up')
              : (isLoading ? 'Logging In...' : 'Log In')
            }
          </button>
        </form>

        <p style={{ marginTop: '24px', textAlign: 'center', color: '#64748b' }}>
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>
                Login here
              </Link>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={switchToSignup}
                style={{
                  color: '#2563eb',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: 'inherit'
                }}
              >
                Sign up
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default Signup;
